/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  styled,
  Tooltip,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CircleErrorIcon, CloseIcon } from '@rsmus/myrsm-icons'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { PbiReportDefinitionViewModel } from '@rsmus/ecp-userservice'
import api from '../../api'
import { Styles } from '../../types'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import TextField from '../TextField/TextField'
import { tokens } from '../../styles/materialTheme'

type Mode = 'create' | 'edit'

interface PowerBIReportModalProps {
  open: boolean
  onClose: () => void
  onSave?: (report: PbiReportDefinitionViewModel) => void
  mode?: Mode
  initialData?: PbiReportDefinitionViewModel
  onUpdate?: (report: PbiReportDefinitionViewModel) => void
  onRemove?: (reportId: number) => void
  lockShortNameOnEdit?: boolean
}

type AuthModelKey =
  | 'isClientLevelAuthorization'
  | 'effectiveIdentityEnabled'
  | 'clientIdInUserToken'

type FormValues = {
  reportShortName: string
  cemAppId: string
  clientSecretName: string
  tenantId: string
  servicePrincipalId: string
  isClientLevelAuthorization: boolean
  effectiveIdentityEnabled: boolean
  effectiveIdentityRoleName: string
  clientIdInUserToken: boolean
}

const styles: Styles = {
  container: (theme) => ({
    padding: '1.5rem',
    [theme.breakpoints.down('tablet')]: {
      paddingBottom: '1.5rem',
    },
  }),
  fieldContainer: (theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      gridTemplateColumns: '1fr',
    },
  }),
  title: (theme) => ({
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    paddingBottom: '1.75rem',
    border: '0',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '2rem',
      paddingTop: '1rem',
    },
  }),
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexFlow: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '1.5rem',
    gap: '0.625rem',
    border: '0',
  },
  errorHeader: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBold,
    fontSize: '1.125rem',
    lineHeight: '1.75rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1.5rem',
      paddingTop: '1rem',
    },
  }),
  errorMessage: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1rem',
      paddingBottom: '1rem',
    },
  }),
  errorContainer: {
    width: '100%',
    height: '7.8125rem',
    padding: '1.5rem',
    backgroundColor: tokens.colors.warning.secondary,
    marginBottom: '1.5rem',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}

const StyledLabel = styled('label')(({ theme }) => ({
  fontFamily: 'Prelo-Black, sans-serif',
  fontSize: '1rem',
  paddingBottom: '0.5rem',
  color: theme.palette.text.primary,
  display: 'block',
}))

/**
 * PowerBIReportModal component for creating and editing Power BI Report Definitions.
 * This modal handles the definition-level properties that are shared across report instances.
 * Instance-specific properties (DisplayName, WorkspaceId, ObjectId, etc.) are managed
 * separately in the PowerBIReportInstanceModal component.
 *
 * @param open - Whether the modal is open
 * @param onClose - Callback when modal is closed
 * @param onSave - Callback when a new report definition is saved
 * @param mode - 'create' or 'edit' mode
 * @param initialData - Initial data for edit mode
 * @param onUpdate - Callback when report definition is updated
 * @param onRemove - Callback when report definition is removed
 * @param lockShortNameOnEdit - Whether to lock the short name field in edit mode (default: true)
 */
const PowerBIReportModal = ({
  open,
  onClose,
  onSave,
  mode = 'create',
  initialData,
  onUpdate,
  onRemove,
  lockShortNameOnEdit = true,
}: PowerBIReportModalProps) => {
  const { t } = useTranslation()
  const methods = useForm<FormValues>({
    defaultValues: {
      reportShortName: '',
      cemAppId: '',
      clientSecretName: '',
      tenantId: '',
      servicePrincipalId: '',
      isClientLevelAuthorization: false,
      effectiveIdentityEnabled: false,
      effectiveIdentityRoleName: '',
      clientIdInUserToken: false,
    },
  })
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  // Track if report instances exist - used to disable IsClientLevelAuthorization toggle
  const [hasInstances, setHasInstances] = useState(false)
  const [effectiveIdentityRoleEnabled, setEffectiveIdentityRoleEnabled] =
    useState(false)

  const letterNumberHyphenRegex = /^[A-Za-z0-9\- ()]+$/
  const guidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

  const formatGUID = (value: string) => value.toUpperCase().trim()

  /**
   * Effect to reset form and fetch instances count when modal opens.
   * In edit mode, checks if any instances exist to determine if
   * IsClientLevelAuthorization can be toggled.
   */

  useEffect(() => {
    const subscription = methods.watch(() => {
      if (hasError) {
        setHasError(false)
        setErrorMessage('')
      }
    })

    return () => subscription.unsubscribe()
  }, [methods, hasError])

  useEffect(() => {
    if (!open) return
    setHasError(false)
    setErrorMessage('')
    setIsSaving(false)
    setHasInstances(false)
    methods.clearErrors()
    if (mode !== 'edit' || !initialData) {
      methods.reset()
      setEffectiveIdentityRoleEnabled(false)
      return
    }

    // Helper function to pick first defined value from object keys
    const pick = (obj: any, ...keys: string[]) =>
      keys.reduce<any>((val, k) => val ?? obj?.[k], undefined)

    // Reset form with definition-specific values from initialData
    methods.reset({
      reportShortName: pick(initialData, 'reportName') ?? '',
      cemAppId: pick(initialData, 'cemAppId', 'appId') ?? '',
      clientSecretName:
        pick(initialData, 'clientSecretName', 'secretName') ?? '',
      tenantId: pick(initialData, 'tenantId') ?? '',
      servicePrincipalId:
        pick(initialData, 'servicePrincipalId', 'clientId') ?? '',
      isClientLevelAuthorization:
        pick(initialData, 'isClientLevelAuthorization') ?? false,
      effectiveIdentityEnabled:
        pick(initialData, 'effectiveIdentityEnabled') ?? false,
      effectiveIdentityRoleName:
        pick(initialData, 'effectiveIdentityRoleName') ?? '',
      clientIdInUserToken: pick(initialData, 'clientIdInUserToken') ?? false,
    })

    // Fetch instances count to determine if auth model can be changed

    setEffectiveIdentityRoleEnabled(
      (pick(initialData, 'effectiveIdentityEnabled') ?? false) ||
        (pick(initialData, 'clientIdInUserToken') ?? false),
    )

    const fetchInstancesCount = async () => {
      if (!initialData?.id) return
      try {
        const filter = {
          pageNumber: 1,
          pageSize: 1,
          sortBy: 'displayName',
          isSortAscending: true,
        }

        const response = await api.user.report_GetAllReportInstances(
          initialData.id,
          filter,
          undefined,
        )
        const instances = response?.data ?? []
        setHasInstances(instances.length > 0)
      } catch (error) {
        console.error('Failed to fetch report instances:', error)
        setHasInstances(false)
      }
    }
    fetchInstancesCount()
  }, [open, mode, initialData, methods])

  /**
   * Handles adding a new Power BI report definition.
   * Creates the report definition without instance-specific properties.
   */

  const setExclusiveAuthModel = (key: AuthModelKey, enabled: boolean) => {
    if (!enabled) {
      methods.setValue(key, false, { shouldDirty: true, shouldValidate: true })

      if (key === 'effectiveIdentityEnabled' || key === 'clientIdInUserToken') {
        const effectiveIdentityEnabled =
          key === 'effectiveIdentityEnabled'
            ? false
            : methods.getValues('effectiveIdentityEnabled')

        const clientIdInUserToken =
          key === 'clientIdInUserToken'
            ? false
            : methods.getValues('clientIdInUserToken')

        setEffectiveIdentityRoleEnabled(
          effectiveIdentityEnabled || clientIdInUserToken,
        )
      }

      return
    }

    methods.setValue(
      'isClientLevelAuthorization',
      key === 'isClientLevelAuthorization',
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    )
    methods.setValue(
      'effectiveIdentityEnabled',
      key === 'effectiveIdentityEnabled',
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    )
    methods.setValue('clientIdInUserToken', key === 'clientIdInUserToken', {
      shouldDirty: true,
      shouldValidate: true,
    })

    setEffectiveIdentityRoleEnabled(
      key === 'effectiveIdentityEnabled' || key === 'clientIdInUserToken',
    )
  }

  const handleAddNewReport = async () => {
    setHasError(false)
    setIsSaving(true)
    const isClientLevelAuthorization = methods.getValues(
      'isClientLevelAuthorization',
    )
    const effectiveIdentityEnabled = methods.getValues(
      'effectiveIdentityEnabled',
    )
    const clientIdInUserToken = methods.getValues('clientIdInUserToken')
    // NOTE: Using 'unknown' intermediate cast because AdminReportDataViewModel type
    // will be updated after NSwag regeneration to match the new PbiReportDefinition schema
    const report: any = {
      cemAppId: methods.getValues('cemAppId').trim(),
      servicePrincipalId: formatGUID(methods.getValues('servicePrincipalId')),
      reportName: methods.getValues('reportShortName').trim(),
      clientSecretName: methods.getValues('clientSecretName').trim(),
      tenantId: formatGUID(methods.getValues('tenantId')),
      isClientLevelAuthorization,
      id: 0,
      effectiveIdentityEnabled,
      effectiveIdentityRoleName: methods
        .getValues('effectiveIdentityRoleName')
        .trim(),
    }
    report.clientIdInUserToken = clientIdInUserToken

    await api.user
      .report_AddPowerBIReport(report)
      .then(() => {
        methods.reset()
        setEffectiveIdentityRoleEnabled(false)
        onSave?.(report)
      })
      .catch(() => {
        setHasError(true)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  /**
   * Handles updating an existing Power BI report definition.
   * Updates definition-level properties only.
   */
  const handleUpdateReport = async () => {
    if (!initialData) return
    setHasError(false)
    setIsSaving(true)
    const isClientLevelAuthorization = methods.getValues(
      'isClientLevelAuthorization',
    )
    const effectiveIdentityEnabled = methods.getValues(
      'effectiveIdentityEnabled',
    )
    const clientIdInUserToken = methods.getValues('clientIdInUserToken')
    // NOTE: Using 'unknown' intermediate cast because AdminReportDataViewModel type
    // will be updated after NSwag regeneration to match the new PbiReportDefinition schema
    const report: any = {
      ...initialData,
      cemAppId: methods.getValues('cemAppId').trim(),
      servicePrincipalId: formatGUID(methods.getValues('servicePrincipalId')),
      reportName: methods.getValues('reportShortName').trim(),
      clientSecretName: methods.getValues('clientSecretName').trim(),
      tenantId: formatGUID(methods.getValues('tenantId')),
      isClientLevelAuthorization,
      effectiveIdentityEnabled,
      effectiveIdentityRoleName: methods
        .getValues('effectiveIdentityRoleName')
        .trim(),
    }
    report.clientIdInUserToken = clientIdInUserToken

    try {
      await api.user.report_UpdatePowerBIReport(
        report as PbiReportDefinitionViewModel,
      )
      methods.reset()
      setEffectiveIdentityRoleEnabled(false)
      onUpdate?.(report as PbiReportDefinitionViewModel)
    } catch (error: unknown) {
      const apiError = error as { response?: string }
      const apiMessage = apiError?.response ?? ''

      setHasError(true)

      if (
        apiMessage.includes(
          'All instance records must be deleted before the security model can be changed',
        )
      ) {
        setErrorMessage(t('PowerBI.ReportModal.SecurityModelCannotChange'))
      } else {
        setErrorMessage(t('PowerBI.ReportModal.ErrorMessage'))
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleOnClose = () => {
    methods.reset()
    setEffectiveIdentityRoleEnabled(false)
    setHasError(false)
    setErrorMessage('')
    onClose()
  }

  const handleCloseIconKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (handleOnClose && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault() // prevent re-opening the dial when enter pressed
        handleOnClose()
      }
    },
    [handleOnClose],
  )

  /**
   * Validates that the input value is a valid GUID format.
   * @param value - The value to validate
   * @returns True if valid, error message if invalid
   */
  const guidValidation = (value: string) => {
    if (!guidRegex.test(value.trim().toUpperCase())) {
      return t('PowerBI.ReportModal.InvalidGUIDError')
    }

    return true
  }

  /**
   * Validates string input against a regex pattern.
   * @param value - The value to validate
   * @param errorText - Error message to return if validation fails
   * @param regex - Regex pattern to test against
   * @returns True if valid, error message if invalid
   */
  const stringValidation = (
    value: string,
    errorText: string,
    regex: RegExp,
  ) => {
    if (!regex.test(value)) {
      return errorText
    }
    return true
  }

  /**
   * Renders the IsClientLevelAuthorization checkbox with conditional tooltip.
   * The checkbox is disabled when instances exist, as the security model
   * cannot be changed while report instances are associated with the definition.
   */
  const renderExclusiveAuthCheckbox = (
    name: AuthModelKey,
    label: string,
    testId: string,
    tooltipText?: string,
  ) => {
    const isDisabled = hasInstances

    const labelNode =
      !isDisabled && tooltipText ? (
        <Tooltip
          title={tooltipText}
          placement="top"
          arrow
          PopperProps={{ disablePortal: false }}
          componentsProps={{ popper: { sx: { zIndex: 20000 } } }}>
          <span style={{ display: 'inline-block', cursor: 'help' }}>
            <StyledLabel htmlFor={name}>{label}</StyledLabel>
          </span>
        </Tooltip>
      ) : (
        <StyledLabel htmlFor={name}>{label}</StyledLabel>
      )

    const checkbox = (
      <Box>
        {labelNode}
        <Box sx={styles.checkboxContainer}>
          <Controller
            name={name}
            control={methods.control}
            render={({ field: { value, ref } }) => (
              <Checkbox
                id={name}
                inputRef={ref}
                checked={!!value}
                onChange={(e) => setExclusiveAuthModel(name, e.target.checked)}
                disabled={isDisabled}
                data-testid={testId}
              />
            )}
          />
        </Box>
      </Box>
    )

    if (isDisabled) {
      return (
        <Tooltip
          title={t(
            'PowerBI.ReportModal.IsClientLevelAuthorizationDisabledTooltip',
          )}
          placement="top"
          arrow
          PopperProps={{ disablePortal: false }}
          componentsProps={{ popper: { sx: { zIndex: 20000 } } }}>
          <Box>{checkbox}</Box>
        </Tooltip>
      )
    }

    return checkbox
  }

  const title =
    mode === 'create'
      ? t('PowerBI.ReportModal.Header')
      : t('PowerBI.ReportModal.EditHeader', 'Edit Power BI report')

  const primaryLabel =
    mode === 'create'
      ? t('PowerBI.ReportModal.AddReport')
      : t('PowerBI.ReportModal.Save', 'Save')

  const onSubmit =
    mode === 'create' ? () => handleAddNewReport() : () => handleUpdateReport()

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={handleOnClose}
        aria-label={title}
        desktopMaxHeight="40rem"
        removeHeaderSpacing
        removeCloseButton
        removeContentSpacing
        data-testid="Lbl_PowerBIReport_AddNewDialog_Modal">
        <FormProvider {...methods}>
          <Box
            component="form"
            sx={styles.container}
            onSubmit={methods.handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                sx={{ padding: 0 }}
                tabIndex={0}
                aria-label={t('ModalDialog.CloseDialog')}
                data-testid="Btn_Modal_Close"
                onClick={handleOnClose}
                onKeyDown={(event: React.KeyboardEvent) =>
                  handleCloseIconKeyDown(event)
                }
                disableFocusRipple
                disableRipple>
                <CloseIcon size={12.5} />
              </IconButton>
            </Box>
            <Box component="h1" sx={styles.title}>
              {title}
            </Box>
            {hasError && (
              <Box sx={styles.errorContainer}>
                <Box
                  sx={{
                    alignItems: 'center',
                    height: '100%',
                    display: 'flex',
                  }}>
                  <CircleErrorIcon
                    size="4rem"
                    color={tokens.colors.rsmGray.copy}
                    fillColor
                  />
                  <Box sx={{ paddingLeft: '1.5rem' }}>
                    <Box
                      component="span"
                      className="title"
                      sx={styles.errorHeader}
                      data-testid="ReportModal_Error_Header">
                      {t('PowerBI.ReportModal.ErrorHeader')}
                    </Box>
                    <Box
                      sx={styles.errorMessage}
                      data-testid="ReportModal_Error_Message">
                      {errorMessage || t('PowerBI.ReportModal.ErrorMessage')}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            <Box sx={styles.fieldContainer}>
              <TextField
                labelName={t('PowerBI.ReportModal.ReportShortName')}
                fieldName="reportShortName"
                componentName="PowerBIReportModal"
                maxLength={50}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersNumbersHyphensError'),
                    letterNumberHyphenRegex,
                  )
                }
                disabled={mode === 'edit' && lockShortNameOnEdit}
              />
              <TextField
                labelName={t('PowerBI.ReportModal.CEMAppId')}
                fieldName="cemAppId"
                componentName="PowerBIReportModal"
                maxLength={50}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersNumbersHyphensError'),
                    letterNumberHyphenRegex,
                  )
                }
              />
              <TextField
                labelName={t('PowerBI.ReportModal.TenantId')}
                fieldName="tenantId"
                componentName="PowerBIReportModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />
              <TextField
                labelName={t('PowerBI.ReportModal.ServicePrincipalId')}
                fieldName="servicePrincipalId"
                componentName="PowerBIReportModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />
              <TextField
                labelName={t('PowerBI.ReportModal.ClientSecretName')}
                fieldName="clientSecretName"
                componentName="PowerBIReportModal"
                maxLength={100}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersColonsError'),
                    /^[A-Za-z:]+$/,
                  )
                }
              />

              {effectiveIdentityRoleEnabled && (
                <TextField
                  labelName={t('PowerBI.ReportModal.EffectiveIdentityRoleName')}
                  fieldName="effectiveIdentityRoleName"
                  componentName="PowerBIReportModal"
                  maxLength={100}
                  fieldRequired={effectiveIdentityRoleEnabled}
                  disabled={!effectiveIdentityRoleEnabled}
                  placeholderText={t('PowerBI.ReportModal.Placeholder')}
                  fieldValidation={(value) => {
                    if (!effectiveIdentityRoleEnabled) return true

                    return stringValidation(
                      value,
                      t('PowerBI.ReportModal.LettersNumbersHyphensError'),
                      letterNumberHyphenRegex,
                    )
                  }}
                />
              )}

              {renderExclusiveAuthCheckbox(
                'isClientLevelAuthorization',
                t('PowerBI.ReportModal.IsClientLevelAuthorization'),
                'Chk_PowerBIReportModal_IsClientLevelAuthorization',
              )}

              {renderExclusiveAuthCheckbox(
                'effectiveIdentityEnabled',
                t('PowerBI.ReportModal.EffectiveIdentityEnabled'),
                'Chk_PowerBIReportModal_EffectiveIdentityEnabled',
                t('PowerBI.ReportModal.EffectiveIdentityEnabledTooltip'),
              )}

              {renderExclusiveAuthCheckbox(
                'clientIdInUserToken',
                t('PowerBI.ReportModal.ClientIdInUserToken'),
                'Chk_PowerBIReportModal_ClientIdInUserToken',
                t('PowerBI.ReportModal.ClientIdInUserTokenTooltip'),
              )}
            </Box>
            <Box sx={styles.buttonContainer}>
              <Button
                variant="outlined"
                data-testid="Btn_AddNewReportDialog_Cancel"
                disabled={isSaving}
                onClick={handleOnClose}>
                {t('PowerBI.ReportModal.Cancel')}
              </Button>
              {mode === 'edit' && (
                <Button
                  variant="outlined"
                  disabled={isSaving}
                  onClick={() => initialData && onRemove?.(initialData.id)}
                  data-testid="Btn_EditReportDialog_RemoveReport">
                  {t('PowerBI.ReportModal.RemoveReport')}
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                data-testid={
                  mode === 'create'
                    ? 'Btn_AddNewReportDialog_AddReport'
                    : 'Btn_EditReportDialog_Save'
                }>
                {primaryLabel}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </ResponsiveModal>
    </Box>
  )
}

export default PowerBIReportModal
